import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import { useDispatch } from "react-redux";
import { guideJobCreated } from "../../../store/slices/firstRunGuideSlice";
import { ArrowLeft } from "lucide-react";
import HelpVideoModal from "../../shared_components/HelpVideoModal";
import { HELP_VIDEOS } from "../../../config/helpVideos";

import SharedStep1_JobDetails from "./SharedStep1_JobDetails";
import SharedStep4_Review from "./SharedStep4_Review";
import CustomStep2_RoundConfig from "./steps/custom/CustomStep2_RoundConfig";
import { useJobDetailsState } from "./steps/custom/hooks/useJobDetailsState";
import { useRoundState } from "./steps/custom/hooks/useRoundState";
import { useRequirementsState } from "./steps/custom/hooks/useRequirementsState";
import { useFocusAreasState } from "./steps/custom/hooks/useFocusAreasState";
import Loader from "../../Loader";
import host from "../../../global";
import LimitExceededModal from "../../shared_components/LimitExceededModal";
import { useTheme } from "../../../hooks/useTheme";
import { experienceLevelToMinMax, minMaxToExperienceLevel } from "./steps/custom/utils/jdUtils";
//Gaurded Navigation from contexts
import { useNavigationGuard } from '../../../contexts/NavigationGuardContext';
import DiscardChangesModal from '../../shared/DiscardChangesModal';


// ─── Small helper components ──────────────────────────────────────────────────

function WeightageHint({ weightages }) {
  const total = Object.values(weightages || {}).reduce((s, w) => s + Number(w || 0), 0);
  const diff = total - 100;
  const msg = diff > 0
    ? `Total is ${total}% — reduce by ${diff}%`
    : `Total is ${total}% — increase by ${Math.abs(diff)}%`;
  return <span className="text-xs text-yellow-500">{msg}</span>;
}

// ─── Module-level helpers & constants ─────────────────────────────────────────

// Maps interview style id → display label (passed to SharedStep4_Review)
const INTERVIEW_TYPE_MAP = {
  screening: "Initial Screening",
  knowledge: "Core Knowledge Evaluation",
  custom: "Bring Your Own Questions",
};

const getInterviewTypeLabel = (styleId) => INTERVIEW_TYPE_MAP[styleId] || styleId;


// ─── Component ────────────────────────────────────────────────────────────────

const CreateJDCustomView = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isEditFlow = searchParams.get("isEditFlow") === "true";

  const MAIN_STEPS = [
    { id: 1, name: isEditFlow ? "Edit a Job Role" : "Create a Job Role" },
    { id: 2, name: "Interview Round" },
    { id: 3, name: "Review" },
  ];
  const assessmentIdParam = searchParams.get("id");
  const isReadonly = searchParams.get("readonly") === "true";
  const accessToken = Cookies.get("accessToken");
  const { theme } = useTheme();
  // ─── State ────────────────────────────────────────────────────────────────

  const [currentStep, setCurrentStep] = useState(1);
  const [isHelpVideoOpen, setIsHelpVideoOpen] = useState(false);
  // sub-steps within step 2: "setup" | "focus" | "weightage" | "requirements"
  const [roundConfigSubStep, setRoundConfigSubStep] = useState("setup");
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [assessmentId, setAssessmentId] = useState(assessmentIdParam || null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [sessionRestored, setSessionRestored] = useState(false);
  const [error, setError] = useState(null);
  const [jDPermissions, setJDPermissions] = useState({
    create_jd_v2_enabled: false,
    create_jd_v2_remaining: 0,
  });
  const [limitModal, setLimitModal] = useState({
    isOpen: false,
    featureKey: null,
    errorCode: null,
  });

  // ─── Step 3 (SharedStep4_Review) state ────────────────────────────────────
  const [preWrittenQuestions, setPreWrittenQuestions] = useState({});
  const [isGeneratingQuestions, setIsGeneratingQuestions] = useState(false);
  const [isJDExpanded, setIsJDExpanded] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [editingIndex, setEditingIndex] = useState(null);

  // Tracks last-committed Step 1 values to detect changes that require downstream reset
  const step1SnapshotRef = useRef({ jobRole: "", experienceLevel: "", responsibilities: "" });

  // Auto-open first skill when questions load (mirrors smart flow behavior)
  useEffect(() => {
    const skillKeys = Object.keys(preWrittenQuestions || {});
    if (skillKeys.length > 0) {
      setExpandedSections((prev) => ({ ...prev, [skillKeys[0]]: true }));
    }
  }, [preWrittenQuestions]);

  // ─── Domain hooks ─────────────────────────────────────────────────────────

  const {
    formData, setFormData, isDragging, isUploadingJD, isGeneratingJD,
    showJobRoleSuggestions, jobRoleSuggestions, selectedSuggestionIndex,
    jobRoleInputRef, jobRoleContainerRef, suggestionsRef, suggestionRefs, fileInputRef,
    updateFormData, handleJobRoleChangeWithSuggestions, handleSuggestionSelect,
    handleJobRoleKeyDown, handleExperienceSelect,
    handleDragOver, handleDragLeave, handleDropJD, handleFileInputChange,
    handleGenerateJDWithAI, isStepOneNextDisabled,
  } = useJobDetailsState({ accessToken });
  
  //Useguard from context to prevent navigation when there are unsaved changes
  const { enableGuard, disableGuard, isBlocked, proceed, reset } = useNavigationGuard(); 
  const isFormDirty =
      !!formData.jobRole.trim() ||
      !!formData.responsibilities.trim() ||
      (formData.requiredSkills?.length ?? 0) > 0 ||
      currentStep > 1;

  useEffect(() => {
    if (isFormDirty) {
        enableGuard();
    } else {
        disableGuard();
      }
    return () => disableGuard();
    }, [isFormDirty, enableGuard, disableGuard]);

  const {
    round, setRound, roundConfigs, setRoundConfigs,
    handleDurationChange, handleFocusChange,
  } = useRoundState({ assessmentId, accessToken });

  const {
    requirementsState, updateRequirementsState, updateRequirementsStateFn,
    handleTopicToggle, handleCustomTopicAdd,
    handleWeightageChange, handleDifficultyChange, handleAutoDistributeWeightage,
  } = useRequirementsState({ round, roundConfigs, currentStep, roundConfigSubStep });

  const { focusAreasCache, updateFocusAreasCache, resetFocusAreasCache } = useFocusAreasState();

  // Destination when leaving the flow (back on step 1, or after step 3 finalize)
  const backDest = isReadonly || location.pathname.startsWith("/hr/")
    ? "/hr/jobvacancies"
    : "/org-admin/created-jds?view=flexi";

  // ─── Derived values ───────────────────────────────────────────────────────

  // Memoized so it's stable as a prop and useCallback dep
  const experienceLevel = useMemo(
    () => minMaxToExperienceLevel(formData.minExp, formData.maxExp),
    [formData.minExp, formData.maxExp]
  );

  // Stable array reference — avoids recreating generateSuggestions on every render
  const allFocusAreas = useMemo(
    () => roundConfigs[round.id]?.focusArray || [],
    [roundConfigs, round.id]
  );

  const [isReplaceJDConfirmOpen, setIsReplaceJDConfirmOpen] = useState(false);

  const isNextDisabled = useMemo(() => {
    if (currentStep === 1) {
      return isStepOneNextDisabled || isGeneratingJD || isUploadingJD || isReplaceJDConfirmOpen;
    }
    if (currentStep === 2) {
      if (roundConfigSubStep === "setup") return !round.type;
      if (roundConfigSubStep === "focus") return !(roundConfigs[round.id]?.focus || "").trim();
      if (roundConfigSubStep === "weightage") {
        const total = Object.values(requirementsState.weightages || {}).reduce((s, w) => s + Number(w || 0), 0);
        return total !== 100;
      }
      if (roundConfigSubStep === "requirements") {
        return !requirementsState.skills.length || !requirementsState.skills.every(
          (skill) => (requirementsState.selectedTopics[skill]?.length || 0) > 0
        );
      }
    }
    return false;
  }, [currentStep, roundConfigSubStep, round, roundConfigs, requirementsState, isStepOneNextDisabled, isGeneratingJD, isUploadingJD, isReplaceJDConfirmOpen]);

  const isProcessing = isLoading || requirementsState.completing;

  // ─── Helpers ──────────────────────────────────────────────────────────────

  // Show a temporary error banner that auto-clears after `ms` milliseconds
  const showError = useCallback((msg, ms = 5000) => {
    setError(msg);
    setTimeout(() => setError(null), ms);
  }, []);

  // ─── Effects ──────────────────────────────────────────────────────────────

  // Fetch JD creation permissions on mount
  useEffect(() => {
    if (!accessToken) return;
    fetch(`${host}/check_permissions_for_jd`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then((r) => r.json())
      .then((data) => {
        setJDPermissions({
          create_jd_v2_enabled: data.create_jd_v2_enabled || false,
          create_jd_v2_remaining: data.create_jd_v2_remaining || 0,
        });
      })
      .catch(() => {});
  }, [accessToken]);

  // Load existing assessment in edit mode
  useEffect(() => {
    if (!assessmentIdParam || !accessToken) {
      setSessionRestored(true);
      return;
    }

    const load = async () => {
      try {
        const res = await fetch(`${host}/api/assessments/${assessmentIdParam}`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!res.ok) throw new Error("Failed to load assessment");
        const data = await res.json();

        if (data.success) {
          const a = data.assessment;
          const firstRound = (a?.rounds || []).find((r) => r && r.id && r.type);
          const { minExp, maxExp } = experienceLevelToMinMax(a?.experienceLevel || "0-1");

          setFormData({
            jobRole: a?.jobRole || "",
            minExp,
            maxExp,
            location: a?.location || "",
            responsibilities: a?.jobDescription || "",
            errors: { jobRole: "", responsibilities: "" },
          });
          // Seed snapshot so first forward navigation from Step 1 doesn't falsely detect a change
          step1SnapshotRef.current = { jobRole: a?.jobRole || "", experienceLevel: a?.experienceLevel || "", responsibilities: a?.jobDescription || "" };
          setAssessmentId(assessmentIdParam);

          if (firstRound) {
            setRound({
              id: firstRound.id,
              type: firstRound.type || "Technical Interview",
              duration: [30, 45, 60, 90].includes(parseInt(firstRound.duration)) ? parseInt(firstRound.duration) : 45,
              focus: firstRound.focus || "",
              title: firstRound.title || "Round 1",
              status: firstRound.status || "setup",
              requirements: firstRound.requirements || [],
              // Use requirements array directly — parseRoundRequirements handles both array and string
              roundRequirements: firstRound.requirements?.length ? firstRound.requirements : null,
            });
            if (firstRound.focus) {
              setRoundConfigs({ [firstRound.id]: { focus: firstRound.focus, focusArray: [] } });
            }
            // Restore pre-written questions so Step 3 review isn't blank on edit
            if (firstRound.sampleQuestions && Object.keys(firstRound.sampleQuestions).length > 0) {
              setPreWrittenQuestions(firstRound.sampleQuestions);
            }
            // Mark completed steps for stepper display (user always starts from step 1 via "Edit Round")
            if (["requirements-completed", "requirements-reviewed"].includes(firstRound.status)) {
              setCompletedSteps(new Set([1, 2]));
            } else if (firstRound.status === "focus-completed") {
              setCompletedSteps(new Set([1]));
            }
          }
        }
      } catch (err) {
        setError("Failed to load assessment");
      } finally {
        setSessionRestored(true);
      }
    };

    load();
  }, [assessmentIdParam, accessToken]); // eslint-disable-line react-hooks/exhaustive-deps


  // ─── Step 3 helpers ───────────────────────────────────────────────────────

  // Generate sample questions from selected topics; called on entering Step 3
  const generateSampleQuestions = useCallback(async () => {
    if (!requirementsState.skills.length) return;
    setIsGeneratingQuestions(true);
    try {
      const skillTopics = Object.fromEntries(
        requirementsState.skills.map((skill) => [
          skill,
          requirementsState.selectedTopics[skill] || [],
        ])
      );
      const res = await fetch(`${host}/generate-sample-questions`, {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          jobrole: formData.jobRole,
          minExp: formData.minExp,
          maxExp: formData.maxExp,
          questionType: "Core Knowledge Evaluation",
          skills: requirementsState.skills,
          skillTopics,
          difficulties: requirementsState.difficulties || {},
        }),
      });
      const data = await res.json();
      if (data.Pre_Written_Questions) setPreWrittenQuestions(data.Pre_Written_Questions);
    } catch (err) {
    } finally {
      setIsGeneratingQuestions(false);
    }
  }, [formData.jobRole, formData.minExp, formData.maxExp, requirementsState.skills,
      requirementsState.selectedTopics, requirementsState.difficulties, accessToken]);

  // Parse the active question index from editingIndex key (format: "skill-N")
  const editingIdx = editingIndex ? parseInt(editingIndex.split("-").pop(), 10) : NaN;

  // Inline question edit — live onChange
  const handleChange = useCallback((e, skill) => {
    const value = e.target.value;
    setPreWrittenQuestions((prev) => {
      const questions = [...(prev[skill] || [])];
      if (!isNaN(editingIdx) && questions[editingIdx]) {
        questions[editingIdx] = { ...questions[editingIdx], question: value };
      }
      return { ...prev, [skill]: questions };
    });
  }, [editingIdx]);

  // Commit on blur/Enter — marks edited:true so interview scheduler won't regenerate this topic
  const handleCommitEdit = useCallback((value, skill) => {
    setPreWrittenQuestions((prev) => {
      const questions = [...(prev[skill] || [])];
      if (!isNaN(editingIdx) && questions[editingIdx]) {
        questions[editingIdx] = { ...questions[editingIdx], question: value, edited: true };
      }
      return { ...prev, [skill]: questions };
    });
  }, [editingIdx]);

  // ─── Navigation handlers ──────────────────────────────────────────────────

  // Persist selected topics and advance to Step 3
  const handleCompleteRequirements = useCallback(async () => {
    if (!round.id || !assessmentId) return;

    const requirements = Object.entries(requirementsState.selectedTopics)
      .filter(([, topics]) => topics.length > 0)
      .map(([skill, topics]) => ({
        skill,
        topics,
        weightage: requirementsState.weightages?.[skill] || 0,
        difficulty: requirementsState.difficulties?.[skill] || "medium",
      }));

    try {
      updateRequirementsState({ loading: true, completing: true });
      const res = await fetch(
        `${host}/api/assessments/${assessmentId}/rounds/${round.id}/requirements`,
        {
          method: "PUT",
          headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
          body: JSON.stringify({
            requirements,
            roundType: round.type,
            onlyForTechnical: round.type === "Technical Interview",
          }),
        }
      );
      const data = await res.json();
      if (data.success) {
        setRound((prev) => ({
          ...prev,
          roundRequirements: requirements,
          requirements,
          status: "requirements-completed",
        }));
        setCompletedSteps((prev) => new Set([...prev, 2]));
        updateRequirementsState({ loading: false });
        if (!Object.keys(preWrittenQuestions).length || requirementsState.topicsChanged) {
          await generateSampleQuestions();
        }
        updateRequirementsState({ completing: false });
        setCurrentStep(3);
      }
    } catch (_) {
      updateRequirementsState({ error: "Failed to save requirements", loading: false, completing: false });
    }
  }, [requirementsState.selectedTopics, round, assessmentId, accessToken, updateRequirementsState, generateSampleQuestions, preWrittenQuestions]);

  // Step 1 → Step 2: save/create assessment and round
  const handleStep1Next = useCallback(async () => {
    if (!jDPermissions.create_jd_v2_enabled) {
      setLimitModal({ isOpen: true, featureKey: "create_jd_v2_enabled", errorCode: "FEATURE_LOCKED" });
      return;
    }
    if (jDPermissions.create_jd_v2_remaining <= 0) {
      setLimitModal({ isOpen: true, featureKey: "create_jd_v2", errorCode: "LIMIT_EXCEEDED" });
      return;
    }

    setIsLoading(true);
    try {
      const url = assessmentId
        ? `${host}/api/assessments/${assessmentId}`
        : `${host}/api/assessments`;
      const res = await fetch(url, {
        method: assessmentId ? "PUT" : "POST",
        headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          jobRole: formData.jobRole,
          experienceLevel,
          jobDescription: formData.responsibilities,
          location: formData.location || "",
        }),
      });
      if (!res.ok) throw new Error("Failed to save assessment");
      const data = await res.json();
      if (!data.success) throw new Error("Failed to save assessment");

      const newAssessmentId = assessmentId || data.assessmentId;
      setAssessmentId(newAssessmentId);

      if (!round.id) {
        const roundRes = await fetch(`${host}/api/assessments/${newAssessmentId}/rounds`, {
          method: "POST",
          headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
          body: JSON.stringify({ type: round.type, duration: String(round.duration), focus: "", title: "Round 1" }),
        });
        if (!roundRes.ok) throw new Error("Failed to create round");
        const roundData = await roundRes.json();
        setRound((prev) => ({ ...prev, id: roundData.roundId, status: "setup" }));
      }

      // If job role or experience changed, clear stale downstream state
      const prevSnapshot = step1SnapshotRef.current;
      if (prevSnapshot.jobRole !== formData.jobRole || prevSnapshot.experienceLevel !== experienceLevel || prevSnapshot.responsibilities !== formData.responsibilities) {
        setPreWrittenQuestions({});
        resetFocusAreasCache();
        setRound((prev) => ({ ...prev, focus: "", status: "setup", roundRequirements: null, requirements: [] }));
        setRoundConfigs((prev) => {
          if (!prev[round.id]) return prev;
          return { ...prev, [round.id]: { focus: "", focusArray: [] } };
        });
        updateRequirementsStateFn((prev) => ({
          ...prev,
          skills: [],
          selectedTopics: {},
          customTopics: {},
          suggestions: {},
          weightages: {},
          difficulties: {},
          topicsChanged: false,
        }));
      }
      step1SnapshotRef.current = { jobRole: formData.jobRole, experienceLevel, responsibilities: formData.responsibilities };

      setCurrentStep(2);
      setRoundConfigSubStep("setup");
      setCompletedSteps((prev) => new Set([...prev, 1]));
    } catch (err) {
      showError(`Failed to proceed: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  }, [jDPermissions, formData, experienceLevel, assessmentId, round, accessToken, showError, resetFocusAreasCache, updateRequirementsStateFn]);

  // Step 2 Next: advances setup → focus → weightage → requirements
  const handleStep2Next = useCallback(async () => {
    if (roundConfigSubStep === "setup") {
      setIsLoading(true);
      try {
        if (round.id && assessmentId) {
          await fetch(`${host}/api/assessments/${assessmentId}/rounds/${round.id}`, {
            method: "PUT",
            headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
            body: JSON.stringify({ type: round.type }),
          });
        }
        setRoundConfigSubStep("focus");
      } catch (_) {
        setRoundConfigSubStep("focus"); // still proceed even if save fails
      } finally {
        setIsLoading(false);
      }
      return;
    }

    if (roundConfigSubStep === "focus") {
      const focusAreas = roundConfigs[round.id]?.focus || "";
      setIsLoading(true);
      try {
        if (round.id) {
          await fetch(`${host}/api/assessments/${assessmentId}/rounds/${round.id}`, {
            method: "PUT",
            headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
            body: JSON.stringify({ focus: focusAreas, status: "focus-completed" }),
          });
          setRound((prev) => ({ ...prev, focus: focusAreas, status: "focus-completed" }));
        } else {
          const res = await fetch(`${host}/api/assessments/${assessmentId}/rounds`, {
            method: "POST",
            headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
            body: JSON.stringify({ type: round.type, duration: String(round.duration), focus: focusAreas, title: "Round 1" }),
          });
          if (!res.ok) throw new Error("Failed to create round");
          const data = await res.json();
          setRound((prev) => ({ ...prev, id: data.roundId, focus: focusAreas, status: "focus-completed" }));
          setRoundConfigs((prev) => ({ ...prev, [data.roundId]: { focus: focusAreas, focusArray: [] } }));
        }
        setRoundConfigSubStep("weightage");
      } catch (err) {
        showError(`Failed to save focus areas: ${err.message}`);
      } finally {
        setIsLoading(false);
      }
      return;
    }

    if (roundConfigSubStep === "weightage") {
      setRoundConfigSubStep("requirements");
      return;
    }

    if (roundConfigSubStep === "requirements") {
      const allHaveTopics = requirementsState.skills.every(
        (skill) => (requirementsState.selectedTopics[skill]?.length || 0) > 0
      );
      if (!allHaveTopics) {
        // This error shows inside the requirements panel, not the top banner
        updateRequirementsState({ error: "Please select at least one topic for each skill." });
        setTimeout(() => updateRequirementsState({ error: null }), 3000);
        return;
      }
      handleCompleteRequirements();
    }
  }, [roundConfigSubStep, round, assessmentId, accessToken, roundConfigs, requirementsState, updateRequirementsState, handleCompleteRequirements, showError]);

  // Step 2 Back: reverses through weightage → focus → setup → step 1
  const handleStep2Prev = useCallback(() => {
    if (roundConfigSubStep === "requirements") return setRoundConfigSubStep("weightage");
    if (roundConfigSubStep === "weightage") return setRoundConfigSubStep("focus");
    if (roundConfigSubStep === "focus") return setRoundConfigSubStep("setup");
    resetFocusAreasCache(); // JD may change on step 1, so discard cached focus areas
    setCurrentStep(1);
    setCompletedSteps((prev) => { const s = new Set(prev); s.delete(2); return s; });
  }, [roundConfigSubStep, resetFocusAreasCache]);

  // Step 3 Next: save assessment config then navigate away
  const handleStep3Next = useCallback(async () => {
    if (isSaving) return;
    setIsSaving(true);
    const topicsToAsk = Object.fromEntries(
      Object.entries(preWrittenQuestions)
        .map(([skill, qs]) => [skill, qs.filter((q) => !q.edited).map((q) => q.topic)])
        .filter(([, topics]) => topics.length > 0)
    );
    try {
      await fetch(
        `${host}/api/assessments/${assessmentId}/rounds/${round.id}/review`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
          body: JSON.stringify({
            requirements: round.requirements || [],
            sampleQuestions: preWrittenQuestions,
            topics_to_ask: topicsToAsk,
          }),
        }
      );
      await dispatch(guideJobCreated());
      navigate(backDest, { state: { showSuccessToast: true } });
    } catch (err) {
      setIsSaving(false);
    }
  }, [isSaving, assessmentId, round, preWrittenQuestions, accessToken, backDest, navigate, dispatch]);

  // Step 3 uses its own nav buttons (SharedStep4_Review) — handleNextStep only covers steps 1 & 2
  const handleNextStep = useCallback(() => {
    if (currentStep === 1) return handleStep1Next();
    if (currentStep === 2) return handleStep2Next();
  }, [currentStep, handleStep1Next, handleStep2Next]);

  const handlePrevStep = useCallback(() => {
    if (currentStep === 1) navigate(backDest);
    else if (currentStep === 2) handleStep2Prev();
  }, [currentStep, backDest, navigate, handleStep2Prev]);

  const handleSubStepNav = useCallback((index) => {
    const keys = ["setup", "focus", "weightage", "requirements"];
    setRoundConfigSubStep(keys[index]);
  }, []);


  // ─── Render ───────────────────────────────────────────────────────────────

  if (!sessionRestored) {
    return (
      <div className="hr-profiles flex items-center justify-center py-20">
        <Loader />
      </div>
    );
  }


  return (
    <div className="min-h-screen">
      <div className="hr-profiles dark:bg-none bg-bgPrimary flex flex-col min-h-screen border border-gray-700/50 rounded-2xl pt-4 pb-16">
        <div className="flex items-center justify-between px-6 pt-2">
          <div className="flex items-center">
            {currentStep === 1 && (
              <button
                onClick={() => navigate(-1)}
                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
                title="Back"
              >
                <ArrowLeft className="w-5 h-5 cursor-pointer dark:text-[#C9A3FF] text-textSecondary" />
              </button>
            )}
            <span className="dark:text-gray-400 text-textSecondary">Custom Mode</span>
          </div>
          <button
            onClick={() => setIsHelpVideoOpen(true)}
            className="w-6 h-6 rounded-full border flex items-center justify-center flex-shrink-0 dark:border-gray-400 border-gray-500 dark:text-gray-400 text-gray-500 text-xs font-semibold hover:opacity-80 transition-opacity"
            title="Help"
          >
            ?
          </button>
        </div>

        {/* Progress stepper */}
        <div className="w-full px-4 py-8">
          <div className="px-12 mx-auto">
            <div className="flex items-center justify-between mx-10">
              {MAIN_STEPS.map((step, index) => {
                const isCompleted = completedSteps.has(step.id);
                const isActive = currentStep === step.id || isCompleted;
                return (
                  <React.Fragment key={step.id}>
                    <div
                      className={`flex items-center gap-4 ${isCompleted || step.id < currentStep ? "cursor-pointer" : "cursor-default"}`}
                      onClick={() => {
                        if (isCompleted || step.id < currentStep) {
                          setCurrentStep(step.id);
                          if (step.id === 2) setRoundConfigSubStep("requirements");
                          if (step.id < 3) setCompletedSteps((prev) => { const s = new Set(prev); s.delete(3); return s; });
                        }
                      }}
                    >
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium border-2 transition-colors duration-200 ${
                          isActive ? "border-[#069855] text-[#069855]" : "border-[#575B72] text-[#575B72] bg-transparent"
                        }`}
                      >
                        {step.id}
                      </div>
                      <div
                        className={`text-base transition-colors duration-200 ${
                          isActive && isCompleted ? "text-[#069855]"
                          : isActive ? "dark:text-white text-textSecondary"
                          : "text-[#575B72]"
                        }`}
                      >
                        {step.name}
                      </div>
                    </div>
                    {index < MAIN_STEPS.length - 1 && (
                      <span className="text-gray-600 h-6 w-6 flex-shrink-0 transition-colors duration-200">{">>"}</span>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
            <div className="w-full bg-gray-700 h-0.5 rounded-full mt-6">
              <div
                className="bg-gradient-to-r from-pink-500 to-blue-500 h-0.5 rounded-full transition-all duration-300"
                style={{ width: `${(currentStep / MAIN_STEPS.length) * 100}%` }}
              />
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className="w-full px-4 mt-4 pb-4 flex-1">

          {/* Top-level error banner */}
          {error && (
            <div className="mb-6 px-12 p-4 border border-red-500/50 rounded-lg">
              <span className="text-red-300 text-sm">{error}</span>
            </div>
          )}

          {/* Step 1 */}
          {currentStep === 1 && (
            <SharedStep1_JobDetails
              formData={formData}
              setFormData={setFormData}
              updateFormData={updateFormData}
              jobRoleInputRef={jobRoleInputRef}
              jobRoleContainerRef={jobRoleContainerRef}
              suggestionsRef={suggestionsRef}
              suggestionRefs={suggestionRefs}
              showJobRoleSuggestions={showJobRoleSuggestions}
              jobRoleSuggestions={jobRoleSuggestions}
              selectedSuggestionIndex={selectedSuggestionIndex}
              handleJobRoleChangeWithSuggestions={handleJobRoleChangeWithSuggestions}
              handleJobRoleKeyDown={handleJobRoleKeyDown}
              handleSuggestionSelect={handleSuggestionSelect}
              handleExperienceSelect={handleExperienceSelect}
              fileInputRef={fileInputRef}
              isDragging={isDragging}
              isUploadingJD={isUploadingJD}
              isGeneratingJD={isGeneratingJD}
              handleDragOver={handleDragOver}
              handleDragLeave={handleDragLeave}
              handleDropJD={handleDropJD}
              handleFileInputChange={handleFileInputChange}
              handleGenerateJDWithAI={handleGenerateJDWithAI}
              onReplaceJDConfirmChange={setIsReplaceJDConfirmOpen}
              isStepOneNextDisabled={isStepOneNextDisabled || isLoading}
              handleAnalyze={handleNextStep}
              onCancel={() => navigate(backDest)}
              hideNavButtons={true}
            />
          )}

          {/* Step 2 */}
          {currentStep === 2 && (
            <div className="px-12">
              <CustomStep2_RoundConfig
                roundConfigSubStep={roundConfigSubStep}
                round={round}
                onDurationChange={(duration) => {
                  resetFocusAreasCache();
                  handleDurationChange(duration);
                  if (!isEditFlow) {
                    handleFocusChange("", []);
                    setRound((prev) => ({ ...prev, focus: "" }));
                    updateRequirementsStateFn((prev) => ({
                      ...prev,
                      skills: [],
                      selectedTopics: {},
                      weightages: {},
                      difficulties: {},
                    }));
                  }
                }}
                jobDescription={formData.responsibilities}
                jobRole={formData.jobRole}
                experienceLevel={experienceLevel}
                currentFocus={roundConfigs[round.id]?.focus || round.focus || ""}
                onFocusChange={handleFocusChange}
                focusAreasCache={focusAreasCache}
                onFocusAreasUpdate={updateFocusAreasCache}
                requirementsState={requirementsState}
                accessToken={accessToken}
                allFocusAreas={allFocusAreas}
                isReadonly={isReadonly}
                onTopicToggle={handleTopicToggle}
                onCustomTopicAdd={handleCustomTopicAdd}
                onStateUpdate={updateRequirementsState}
                onStateUpdateFn={updateRequirementsStateFn}
                onWeightageChange={handleWeightageChange}
                onDifficultyChange={handleDifficultyChange}
                onAutoDistributeWeightage={handleAutoDistributeWeightage}
                onSubStepNav={handleSubStepNav}
              />
            </div>
          )}

          {/* Step 3 — Review */}
          {currentStep === 3 && (
            <SharedStep4_Review
                formData={{
                  ...formData,
                  interview_timelimit: round.duration,
                  generatedJD: { job_description: formData.responsibilities },
                  Pre_Written_Questions: preWrittenQuestions,
                }}
                selectedStyle="knowledge"
                showSubStepBreadcrumb={true}
                allSkills={requirementsState.skills}
                skillDifficulties={requirementsState.difficulties || {}}
                isUploadingJD={isGeneratingQuestions}
                isJDExpanded={isJDExpanded}
                setIsJDExpanded={setIsJDExpanded}
                expandedSections={expandedSections}
                setExpandedSections={setExpandedSections}
                editingIndex={editingIndex}
                setEditingIndex={setEditingIndex}
                onBack={() => {
                  setCurrentStep(2);
                  setRoundConfigSubStep("requirements");
                  setCompletedSteps((prev) => { const s = new Set(prev); s.delete(3); return s; });
                }}
                onNext={handleStep3Next}
                isNextLoading={isSaving}
                handleChange={handleChange}
                handleCommitEdit={handleCommitEdit}
                getInterviewTypeLabel={getInterviewTypeLabel}
                onSubStepNav={(index) => {
                  handleSubStepNav(index);
                  setCurrentStep(2);
                  setCompletedSteps((prev) => { const s = new Set(prev); s.delete(3); return s; });
                }}
              />
          )}

          {/* Bottom navigation — hidden on Step 3 (SharedStep4_Review has its own nav) */}
          {currentStep !== 3 && (
            <div className="w-full px-12 py-4 flex items-center justify-between">
              {currentStep !== 1 ? (
                <button
                  onClick={handlePrevStep}
                  disabled={isLoading || requirementsState.loading}
                  className={`${theme !== 'dark' ? 'border-btn !text-textSecondary' : 'button'} flex items-center gap-2 rounded-full dark:!text-white dark:border dark:border-gray-600 hover:border-gray-400 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  <span>Back</span>
                </button>
              ) : (
                <div />
              )}

              <div className="flex items-center gap-3">
                {roundConfigSubStep === "weightage" && isNextDisabled && (
                  <WeightageHint weightages={requirementsState.weightages} />
                )}
                <button
                  onClick={handleNextStep}
                  disabled={isNextDisabled || isProcessing}
                  className={`button px-6 py-3 rounded-full text-white transition-all duration-200 active:scale-95 ${
                    isNextDisabled || isProcessing ? "opacity-50 cursor-not-allowed pointer-events-none" : ""
                  }`}
                >
                  <span>{isProcessing ? "Processing..." : "Next"}</span>
                </button>
              </div>
            </div>
          )}

        </div>
      </div>

      <LimitExceededModal
        isOpen={limitModal.isOpen}
        onClose={() => setLimitModal({ isOpen: false, featureKey: null, errorCode: null })}
        featureKey={limitModal.featureKey}
        errorCode={limitModal.errorCode}
      />

      <DiscardChangesModal
              isOpen={isBlocked}
              onCancel={() => reset()}
              onConfirm={() => {
                localStorage.removeItem('createJDFormData');
                localStorage.removeItem('createJDSections');
                proceed();
              }}
      />

      <HelpVideoModal
        open={isHelpVideoOpen}
        onClose={() => setIsHelpVideoOpen(false)}
        videos={HELP_VIDEOS}
        defaultIndex={1}
      />
    </div>
  );
};

export default CreateJDCustomView;
